import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { DataTable, ColumnDef } from '@/components/data-display/DataTable';
import { StatusBadge } from '@/components/data-display/StatusBadge';
import { Badge } from '@/components/ui/Badge';
import { mockNfaLfaService } from '@/services/mock/MockNfaLfaService';
import { LetterForAward } from '@/types/domain';
import { formatDate } from '@/lib/utils';
import {
  Award,
  Clock,
  CheckCircle2,
  Eye,
  Pencil,
  Trash2,
  Building2,
  AlertTriangle,
  Loader2,
  X,
  Truck,
} from 'lucide-react';
import { ROUTES } from '@/constants/routes';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SlideOverDrawer } from '@/components/ui/SlideOverDrawer';
import { LfaDetailsPage } from './LfaDetailsPage';

const formatInr = (val: number) =>
  `₹${Number(val || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatInrCompact = (amount: number) => {
  if (amount >= 10000000) {
    return `₹${(amount / 10000000).toFixed(2)} Cr`;
  }
  if (amount >= 100000) {
    return `₹${(amount / 100000).toFixed(2)} L`;
  }
  return `₹${Number(amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

export const LfaListPage: React.FC = () => {
  const navigate = useNavigate();
  const [lfas, setLfas] = useState<LetterForAward[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState<string>('ALL');

  // Deletion State
  const [lfaToDelete, setLfaToDelete] = useState<LetterForAward | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedLfaForDrawer, setSelectedLfaForDrawer] = useState<string | null>(null);

  const loadLfas = async () => {
    try {
      setIsLoading(true);
      const data = await mockNfaLfaService.getLfas();
      const filtered =
        selectedStatus === 'ALL'
          ? data
          : data.filter((item) => item.status === selectedStatus);
      setLfas(filtered);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadLfas();
  }, [selectedStatus]);

  // Lock body scroll when delete modal is open
  useEffect(() => {
    if (!lfaToDelete) return;
    const orig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = orig;
    };
  }, [lfaToDelete]);

  const handleConfirmDelete = async () => {
    if (!lfaToDelete) return;
    const targetId = lfaToDelete.id;
    try {
      setIsDeleting(true);
      setLfas((prev) => prev.filter((item) => item.id !== targetId));
      await mockNfaLfaService.deleteLfa(targetId);
      setLfaToDelete(null);
    } catch (err) {
      console.error('Failed to delete LFA:', err);
      loadLfas();
    } finally {
      setIsDeleting(false);
    }
  };

  // Aggregate KPI metrics
  const totalCount = lfas.length;
  const underReviewCount = lfas.filter((l) => l.status === 'UnderReview').length;
  const awardedCount = lfas.filter((l) => l.status === 'Awarded').length;
  const totalValue = lfas.reduce((sum, l) => sum + (l.awardedValue || 0), 0);
  const uniqueVendors = new Set(lfas.map((l) => l.vendorName)).size;

  const columns: ColumnDef<LetterForAward>[] = [
    {
      header: 'LFA Number',
      accessorKey: 'lfaNumber',
      sortable: true,
      cell: (row) => (
        <div className="space-y-0.5 font-poppins">
          <span
            onClick={() => navigate(ROUTES.LFA.DETAILS(row.id))}
            className="font-poppins font-bold text-primary hover:underline cursor-pointer whitespace-nowrap text-sm block"
          >
            {row.lfaNumber}
          </span>
          <span className="text-xs text-foreground-muted font-mono block whitespace-nowrap">
            {formatDate(row.effectiveDate)} • NFA: {row.nfaNumber}
          </span>
        </div>
      ),
    },
    {
      header: 'Award Scope & Title',
      accessorKey: 'title',
      cell: (row) => (
        <div className="flex flex-col min-w-0 max-w-[190px] xl:max-w-[220px] font-poppins">
          <span
            className="font-bold text-foreground text-sm truncate block"
            title={row.title}
          >
            {row.title}
          </span>
          <span className="text-xs text-foreground-secondary font-mono truncate block">
            RFQ: {row.rfqNumber || 'RFQ-STD'} • REQ: {row.requestNumber}
          </span>
        </div>
      ),
    },
    {
      header: 'Awarded Supplier',
      accessorKey: 'vendorName',
      cell: (row) => (
        <div className="max-w-[140px] xl:max-w-[170px] truncate font-poppins" title={row.vendorName}>
          <span className="text-sm font-semibold text-foreground truncate block flex items-center gap-1">
            <Building2 className="h-3.5 w-3.5 text-primary shrink-0" />
            {row.vendorName}
          </span>
          <span className="text-xs text-foreground-muted font-mono block">
            ID: {row.vendorCode}
          </span>
        </div>
      ),
    },
    {
      header: 'Commercial Terms',
      cell: (row) => (
        <div className="space-y-1 font-poppins whitespace-nowrap">
          <Badge variant="outline" className="text-xs font-bold font-poppins px-2 py-0.5 flex items-center gap-1 w-fit">
            <Truck className="h-3 w-3 text-primary" /> {row.deliveryTerms || 'DDP'}
          </Badge>
          <span className="text-[11px] text-foreground-muted block font-medium">
            {row.paymentTerms || 'Net 30'}
          </span>
        </div>
      ),
    },
    {
      header: 'Awarded Value',
      accessorKey: 'awardedValue',
      cell: (row) => (
        <div className="font-poppins whitespace-nowrap">
          <span className="font-bold text-foreground font-mono text-sm block">
            {formatInr(row.awardedValue)}
          </span>
          <span className="text-[11px] text-foreground-muted block font-mono">
            Expires: {formatDate(row.expiryDate)}
          </span>
        </div>
      ),
    },
    {
      header: 'Status',
      accessorKey: 'status',
      cell: (row) => (
        <div className="flex items-center font-poppins">
          <StatusBadge
            status={row.status}
            category="LFA"
            className="w-32 py-1 justify-center text-center font-semibold text-xs rounded-full shadow-2xs font-poppins whitespace-nowrap"
          />
        </div>
      ),
    },
    {
      header: 'Actions',
      cell: (row) => (
        <div
          className="flex items-center gap-1.5 justify-start shrink-0 whitespace-nowrap font-poppins min-w-[110px]"
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedLfaForDrawer(row.id)}
            title="View Award Dossier (Slide-over)"
            className="h-8 w-8 p-0 rounded-lg hover:bg-primary/15 hover:text-primary active:scale-90 transition-all duration-200 cursor-pointer text-foreground-secondary shrink-0 inline-flex items-center justify-center group shadow-2xs hover:shadow-xs"
          >
            <Eye className="h-4 w-4 transition-transform duration-200 group-hover:scale-115" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(ROUTES.LFA.EDIT(row.id))}
            title="Edit Award Terms"
            className="h-8 w-8 p-0 rounded-lg hover:bg-primary/10 cursor-pointer text-primary hover:text-primary-dark shrink-0 inline-flex items-center justify-center"
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLfaToDelete(row)}
            title="Delete Award"
            className="h-8 w-8 p-0 rounded-lg text-rose-500 hover:text-rose-600 hover:bg-rose-500/15 cursor-pointer shrink-0 inline-flex items-center justify-center"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 pb-12 font-poppins">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-foreground flex items-center gap-2.5 font-poppins">
            <Award className="h-7 w-7 text-primary" /> Letters For Award (LFA)
          </h1>
          <p className="text-xs text-foreground-secondary font-medium mt-1 font-poppins">
            Formal commercial award agreements and letters of intent issued to winning suppliers authorizing project mobilization prior to Purchase Order release.
          </p>
        </div>
      </div>

      {/* KPI Metric Summary Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3.5 font-poppins">
        <Card className="rounded-2xl border border-border p-4 space-y-1 bg-card shadow-2xs">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-wider text-foreground-muted">Total Awards</span>
            <div className="p-1.5 rounded-lg bg-primary/10 text-primary">
              <Award className="h-4 w-4" />
            </div>
          </div>
          <span className="text-xl font-extrabold text-foreground block">{totalCount}</span>
          <span className="text-[10px] text-foreground-muted">Committed contracts</span>
        </Card>

        <Card className="rounded-2xl border border-border p-4 space-y-1 bg-card shadow-2xs">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-wider text-foreground-muted">Under Review</span>
            <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-600">
              <Clock className="h-4 w-4" />
            </div>
          </div>
          <span className="text-xl font-extrabold text-amber-600 block">{underReviewCount}</span>
          <span className="text-[10px] text-foreground-muted">Vendor countersign pending</span>
        </Card>

        <Card className="rounded-2xl border border-border p-4 space-y-1 bg-card shadow-2xs">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-wider text-foreground-muted">Mobilized</span>
            <div className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-600">
              <CheckCircle2 className="h-4 w-4" />
            </div>
          </div>
          <span className="text-xl font-extrabold text-emerald-600 block">{awardedCount}</span>
          <span className="text-[10px] text-foreground-muted">Ready for PO release</span>
        </Card>

        <Card className="rounded-2xl border border-border p-4 space-y-1 bg-card shadow-2xs">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-wider text-foreground-muted">Awarded Value</span>
            <div className="p-1.5 rounded-lg bg-blue-500/10 text-blue-600 font-mono font-bold text-xs">
              ₹
            </div>
          </div>
          <span className="text-xl font-extrabold text-foreground block font-mono">
            {formatInrCompact(totalValue)}
          </span>
          <span className="text-[10px] text-foreground-muted">Committed supplier spend</span>
        </Card>

        <Card className="rounded-2xl border border-border p-4 space-y-1 bg-card shadow-2xs">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-wider text-foreground-muted">Active Suppliers</span>
            <div className="p-1.5 rounded-lg bg-purple-500/10 text-purple-600">
              <Building2 className="h-4 w-4" />
            </div>
          </div>
          <span className="text-xl font-extrabold text-foreground block">
            {uniqueVendors}
          </span>
          <span className="text-[10px] text-foreground-muted">Contracted vendor base</span>
        </Card>
      </div>

      {/* Main Ledger Card */}
      <Card className="border-border rounded-2xl shadow-xs overflow-hidden font-poppins">
        <CardHeader className="p-4 border-b border-border/60 bg-muted/20">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            {/* Status Tabs */}
            <div className="flex flex-wrap items-center gap-1.5">
              {[
                { key: 'ALL', label: 'All Awards' },
                { key: 'Awarded', label: 'Awarded' },
                { key: 'UnderReview', label: 'Under Review' },
                { key: 'Draft', label: 'Draft' },
                { key: 'Cancelled', label: 'Cancelled' },
              ].map((tab) => {
                const isActive = selectedStatus === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setSelectedStatus(tab.key)}
                    className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all cursor-pointer font-poppins ${
                      isActive
                        ? 'bg-primary text-primary-foreground font-bold shadow-xs'
                        : 'bg-card border border-border text-foreground-secondary hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-4 overflow-x-hidden">
          <DataTable
            data={lfas}
            columns={columns}
            isLoading={isLoading}
            searchPlaceholder="Search LFA #, Supplier name, NFA #..."
            fixedHeight={true}
            containerHeightClass="max-h-[580px] overflow-y-auto overflow-x-hidden"
            emptyTitle="No Letters for Award Found"
            emptyDescription="There are no commercial award records matching your current filter criteria."
            onRowClick={(row) => navigate(ROUTES.LFA.DETAILS(row.id))}
          />
        </CardContent>
      </Card>

      {/* Delete Confirmation Modal (Mounted via createPortal to document.body) */}
      {lfaToDelete &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 animate-in fade-in"
            onClick={() => !isDeleting && setLfaToDelete(null)}
          >
            <div
              className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl space-y-4 font-poppins animate-in zoom-in-95 my-auto text-foreground"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-border/60 pb-3">
                <div className="flex items-center gap-2 text-rose-600">
                  <AlertTriangle className="h-5 w-5" />
                  <h3 className="text-base font-bold text-foreground font-poppins">
                    Delete Letter for Award?
                  </h3>
                </div>
                <button
                  onClick={() => !isDeleting && setLfaToDelete(null)}
                  className="rounded-lg p-1 hover:bg-muted text-foreground-muted cursor-pointer"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Dossier Details Box */}
              <div className="rounded-xl bg-muted/40 border border-border p-3.5 space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-foreground-muted uppercase tracking-wider text-[10px]">
                    LFA Reference
                  </span>
                  <span className="font-mono font-bold text-primary">
                    {lfaToDelete.lfaNumber}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-foreground-muted uppercase tracking-wider text-[10px]">
                    Awarded Supplier
                  </span>
                  <span className="font-bold text-foreground truncate max-w-[200px] text-right">
                    {lfaToDelete.vendorName}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-foreground-muted uppercase tracking-wider text-[10px]">
                    Awarded Commitment
                  </span>
                  <span className="font-bold text-foreground font-mono">
                    {formatInr(lfaToDelete.awardedValue)}
                  </span>
                </div>
              </div>

              <p className="text-xs text-foreground-secondary leading-relaxed">
                Are you sure you want to delete this Letter for Award? This action removes the commercial award record and cannot be undone.
              </p>

              <div className="flex items-center justify-end gap-3 pt-2 border-t border-border/60">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isDeleting}
                  onClick={() => setLfaToDelete(null)}
                  className="cursor-pointer font-bold rounded-xl font-poppins"
                >
                  Cancel
                </Button>
                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={handleConfirmDelete}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 active:scale-95 text-white font-extrabold text-xs shadow-lg shadow-rose-600/30 transition-all cursor-pointer disabled:opacity-50 font-poppins"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-3.5 w-3.5" />
                      <span>Delete Award</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Right Slide-Over Curtain Drawer for LFA Dossier */}
      <SlideOverDrawer
        isOpen={Boolean(selectedLfaForDrawer)}
        onClose={() => setSelectedLfaForDrawer(null)}
        title="Letter for Award (LFA) Dossier"
        subtitle="Formal contract commitment, commercial award terms, SLA conditions, and vendor counter-acceptance"
        fullPageUrl={selectedLfaForDrawer ? ROUTES.LFA.DETAILS(selectedLfaForDrawer) : undefined}
      >
        {selectedLfaForDrawer && (
          <LfaDetailsPage
            recordId={selectedLfaForDrawer}
            isDrawer={true}
            onClose={() => setSelectedLfaForDrawer(null)}
          />
        )}
      </SlideOverDrawer>
    </div>
  );
};
